import { useTranslation } from "react-i18next";
import type { ListedTalkStep } from "../../../api/content/workflowsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import Field from "../../../ui/forms/Field";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import type { OptionalOffer } from "../draft/authoringOptions";

interface ReviewTalkChoiceProps {
  /** The talk step the review offers, pinned; `undefined` for none. */
  talk: string | undefined;
  /** The talk steps the project can use. */
  offered: OptionalOffer<readonly ListedTalkStep[]>;
  onChange: (talk: string | undefined) => void;
}

/**
 * Whether a review offers Talk with the agent (F105; Johannes, 4 Nov: "the same flexibility to add
 * an optional review step at any point"): on, it pins a talk step the project can use. One talk
 * step is a checkbox, several a select. A pin the project no longer offers stays until it is turned off.
 */
export default function ReviewTalkChoice({ talk, offered, onChange }: ReviewTalkChoiceProps) {
  const { t } = useTranslation();
  const steps = offered.status === "loaded" ? offered.value : [];
  const failure = offered.status === "failed" ? <ErrorNotice error={offered.error} compact={true} /> : null;
  if (steps.length > 1) {
    const pinnedItem = talk?.split("@")[0];
    return (
      <Field layout="inline" label={t("authoring.step.human.talk")} errors={failure}>
        {(control) => (
          <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={talk ?? ""} onChange={(event) => { onChange(event.target.value === "" ? undefined : event.target.value); }}>
            <option value="">{t("authoring.step.human.noTalk")}</option>
            {/* A pinned talk step keeps its version until another is chosen. */}
            {steps.map((step) => {
              const value = step.reference.split("@")[0] === pinnedItem && talk !== undefined ? talk : step.reference;
              return <option key={step.reference} value={value}>{step.name}</option>;
            })}
            {talk !== undefined && !steps.some((step) => step.reference.split("@")[0] === pinnedItem) ? <option value={talk}>{t("authoring.step.human.talk")}</option> : null}
          </select>
        )}
      </Field>
    );
  }
  const only = steps[0];
  if (only === undefined && talk === undefined && failure === null) {
    return null;
  }
  return (
    <>
      <OptionLabel>
        <input type="checkbox" checked={talk !== undefined} disabled={talk === undefined && only === undefined} onChange={(event) => { onChange(event.target.checked ? only?.reference : undefined); }} />
        {t("authoring.step.human.talk")}
      </OptionLabel>
      {failure}
    </>
  );
}
